import { useState, useEffect } from 'react';
import { IconChevronRight } from '@tabler/icons-react';
import { ActionIcon, Checkbox, Combobox, Group, TextInput, useCombobox } from '@mantine/core';
import classes from './TransferList.module.css';

function RenderList({ 
  options, 
  onTransfer, 
  type, 
  title, 
  filter, 
  searchPlaceholder = "Search items",
  disabled = false 
}) {
  const combobox = useCombobox();
  const [selectedValues, setSelectedValues] = useState([]);
  const [search, setSearch] = useState('');

  const handleValueSelect = (val) =>
    setSelectedValues((current) =>
      current.includes(val) ? current.filter((v) => v !== val) : [...current, val]
    );

  const filteredItems = options.filter((item) => {
    if (filter && typeof filter === 'function') {
      return filter(search, item);
    }
    // Default filter behavior
    return item.label?.toLowerCase().includes(search.toLowerCase().trim()) ||
           item.description?.toLowerCase().includes(search.toLowerCase().trim());
  });

  const items = filteredItems.map((item) => (
    <Combobox.Option
      value={item.value}
      key={item.value}
      active={selectedValues.includes(item.value)}
      onMouseOver={() => combobox.resetSelectedOption()}
      style={{ color: '#000', backgroundColor: '#fff' }}
    >
      <Group gap="sm">
        <Checkbox
          checked={selectedValues.includes(item.value)}
          onChange={() => {}}
          aria-hidden
          tabIndex={-1}
          style={{ pointerEvents: 'none' }}
        />
        <div style={{ color: '#000' }}>
          <div style={{ color: '#000' }}>{item.label}</div>
          {item.description && (
            <div style={{ 
              fontSize: '0.875rem', 
              color: '#666',
              opacity: 0.7 
            }}>
              {item.description}
            </div>
          )}
        </div>
      </Group>
    </Combobox.Option>
  ));

  const handleTransfer = () => {
    const selectedItems = options.filter(item => selectedValues.includes(item.value));
    onTransfer(selectedItems);
    setSelectedValues([]);
  };

  return (
    <div className={classes.renderList} data-type={type}>
      <div className={classes.header}>
        {title && <div className={classes.title} style={{ color: '#000', backgroundColor: '#f8f9fa' }}>{title}</div>}
      </div>
      <Combobox store={combobox} onOptionSubmit={handleValueSelect} disabled={disabled}>
        <Combobox.EventsTarget>
          <Group wrap="nowrap" gap={0} className={classes.controls}>
            <TextInput
              placeholder={searchPlaceholder}
              classNames={{ input: classes.input }}
              value={search}
              onChange={(event) => {
                setSearch(event.currentTarget.value);
                combobox.updateSelectedOptionIndex();
              }}
              disabled={disabled}
            />
            <ActionIcon
              radius={0}
              variant="default"
              size={36}
              className={classes.control}
              onClick={handleTransfer}
              disabled={disabled || selectedValues.length === 0}
            >
              <IconChevronRight className={classes.icon} />
            </ActionIcon>
          </Group>
        </Combobox.EventsTarget>
        <div className={classes.list} style={{ backgroundColor: '#fff' }}>
          <Combobox.Options>
            {items.length > 0 ? items : <Combobox.Empty style={{ color: '#666' }}>Nothing found...</Combobox.Empty>}
          </Combobox.Options>
        </div>
      </Combobox>
    </div>
  );
}

export function TransferList({ 
  value = [[], []], 
  onChange, 
  titles = ['Available', 'Selected'],
  listHeight = 224,
  filter,
  searchPlaceholder = "Search items",
  disabled = false 
}) {
  const [data, setData] = useState(value);

  // Update internal state when value prop changes
  useEffect(() => {
    setData(value);
  }, [value]);

  const handleTransfer = (transferFrom, selectedItems) => {
    setData((current) => {
      const transferTo = transferFrom === 0 ? 1 : 0;
      
      // Remove selected items from source list
      const transferFromData = current[transferFrom].filter(
        (item) => !selectedItems.some(selected => selected.value === item.value)
      );
      
      // Add selected items to destination list
      const transferToData = [...current[transferTo], ...selectedItems];
      
      const result = [];
      result[transferFrom] = transferFromData;
      result[transferTo] = transferToData;
      
      // Call onChange if provided
      if (onChange) {
        onChange(result);
      }
      
      return result;
    });
  };

  return (
    <div className={classes.root}>
      <RenderList
        type="forward"
        options={data[0]}
        onTransfer={(selectedItems) => handleTransfer(0, selectedItems)}
        title={titles[0]}
        filter={filter}
        searchPlaceholder={searchPlaceholder}
        disabled={disabled}
      />
      <RenderList
        type="backward"
        options={data[1]}
        onTransfer={(selectedItems) => handleTransfer(1, selectedItems)}
        title={titles[1]}
        filter={filter}
        searchPlaceholder={searchPlaceholder}
        disabled={disabled}
      />
    </div>
  );
}